import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useStatusOptions } from '../lib/useStatusOptions';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { MagnifyingGlass, Plus, Trash, FunnelSimple, UsersThree, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

// US state codes — shown in the State value dropdown when picking
// `address_state` as a filter field. Backend matching is already
// case-insensitive (regex `i` flag), so typing "tx" still matches "TX"
// — these are just the canonical pick list.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
  // US territories commonly used in CRM data
  'PR','GU','VI','AS','MP',
];

const BASE_FIELDS = [
  { value: 'first_name', label: 'First Name', defaultOperator: '*' },
  { value: 'last_name', label: 'Last Name', defaultOperator: '*' },
  { value: 'company', label: 'Company', defaultOperator: '*' },
  { value: 'email', label: 'Email', defaultOperator: '~' },
  { value: 'phone', label: 'Phone', defaultOperator: '~' },
  { value: 'status', label: 'ID/Status', valueOptions: ['Lead', 'Customer', 'Prospect'] },
  { value: 'address_city', label: 'City', defaultOperator: '*' },
  { value: 'address_state', label: 'State', valueOptions: US_STATES },
  { value: 'address_zip', label: 'ZIP Code', defaultOperator: '*' },
  { value: 'address_country', label: 'Country', defaultOperator: '*' },
  { value: 'tags', label: 'Tags', dynamicOptions: 'tags' },
  { value: 'notes', label: 'Notes', defaultOperator: '~' },
];

// Activity-history fields use a different operator set ("days_since" / "days_within" / "never")
// "Last Activity (any)" is at the top since that's what most users actually want.
const ACTIVITY_FIELDS = [
  { value: 'last_activity_at', label: 'Last Activity (any)' },
  { value: 'last_call_at', label: 'Last Call' },
  { value: 'last_email_at', label: 'Last Email' },
  { value: 'last_meeting_at', label: 'Last Meeting' },
  { value: 'last_note_at', label: 'Last Note' },
];
const ACTIVITY_FIELD_VALUES = new Set(ACTIVITY_FIELDS.map(f => f.value));

// Operators are split into two sets — text operators are intuitive to most
// users and shown for name/email/company/etc. The numeric operators (<, >,
// <=, >=) only appear for date/number user fields where they actually make
// sense; hiding them for text fields removes a major source of confusion.
const TEXT_OPERATORS = [
  { value: '=', label: 'equals' },
  { value: '~', label: 'contains' },
  { value: '*', label: 'starts with' },
  { value: '<>', label: 'not equal' },
];
const NUMERIC_OPERATORS = [
  ...TEXT_OPERATORS,
  { value: '<', label: 'less than' },
  { value: '>', label: 'greater than' },
  { value: '<=', label: 'less or equal' },
  { value: '>=', label: 'greater or equal' },
];

const ACTIVITY_OPERATORS = [
  { value: 'days_since', label: "hasn't happened in (days)" },
  { value: 'days_within', label: 'happened within last (days)' },
  { value: 'never', label: 'never happened' },
];

// Sort options shown after a lookup runs — lets the user re-order the
// matching contacts without leaving the dialog. Defaults to Last Name asc.
const SORT_OPTIONS = [
  { value: 'last_name:asc', label: 'Last Name (A→Z)' },
  { value: 'last_name:desc', label: 'Last Name (Z→A)' },
  { value: 'first_name:asc', label: 'First Name (A→Z)' },
  { value: 'first_name:desc', label: 'First Name (Z→A)' },
  { value: 'company:asc', label: 'Company (A→Z)' },
  { value: 'company:desc', label: 'Company (Z→A)' },
  { value: 'created_at:desc', label: 'Newest First' },
  { value: 'created_at:asc', label: 'Oldest First' },
];

export default function LookupDialog({ open, onOpenChange, onResults, currentIds = [], currentTotal = 0, currentQuery = null }) {
  const { statuses: statusOptions } = useStatusOptions();
  const [conditions, setConditions] = useState([
    { field: 'last_name', operator: '=', value: '' }
  ]);
  const [logic, setLogic] = useState('and');
  const [mode, setMode] = useState('new'); // 'new' | 'narrow' | 'add'
  const [searching, setSearching] = useState(false);
  const [resultCount, setResultCount] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {contacts:[ids], _query:{...}}
  const [userFields, setUserFields] = useState([]);
  const [dropdownLists, setDropdownLists] = useState([]);
  // Distinct tags currently in use across all contacts — used to populate the
  // Tags dropdown so users don't have to remember exact tag spellings.
  const [tagsList, setTagsList] = useState([]);
  // Sort order for results — defaults to Last Name asc, can be changed after
  // a lookup runs to re-sort the result set.
  const [sortKey, setSortKey] = useState('last_name:asc');

  // When dialog re-opens, default to 'narrow' ONLY if there's a real previous
  // lookup query to narrow against. If the user is on the wide "My Contacts"
  // universe (no previous conditions), default to 'new' so the search hits the
  // entire DB instead of only the ~200 lazy-loaded IDs currently in memory.
  useEffect(() => {
    if (!open) return;
    if (currentQuery?.conditions?.length) {
      setMode('narrow');
    } else {
      setMode('new');
    }
  }, [open, currentQuery]);

  // Load user-field metadata + reusable lists so Lookup knows about dropdowns + custom labels
  useEffect(() => {
    api.get('/settings/user-fields')
      .then(({ data }) => setUserFields(data.fields || []))
      .catch(() => {});
    api.get('/dropdown-lists')
      .then(({ data }) => setDropdownLists(data || []))
      .catch(() => {});
    // Tags are pulled fresh each open in case new tags were added since last open
  }, []);

  // Re-fetch tag list every time the dialog opens (so freshly-added tags show)
  useEffect(() => {
    if (!open) return;
    api.get('/tags')
      .then(({ data }) => {
        // Endpoint returns [{tag, count}, ...] sorted by usage desc
        const tags = (data || []).map(t => t.tag).filter(Boolean);
        setTagsList(tags);
      })
      .catch(() => setTagsList([]));
  }, [open]);

  // Build full field list: base + user fields (with admin-chosen labels)
  const allFields = [
    ...BASE_FIELDS,
    ...userFields.map(f => ({ value: f.id, label: f.label, meta: f })),
  ];

  // Resolve a field's options — prefers list_id reference (Phase B), else inline options
  const getFieldMeta = (fieldValue) => {
    const meta = allFields.find(f => f.value === fieldValue)?.meta || null;
    if (!meta) return null;
    if (meta.list_id) {
      const list = dropdownLists.find(l => l.list_id === meta.list_id);
      return { ...meta, options: list?.options || [] };
    }
    return meta;
  };

  // Return the right operator list for a given field. Text fields hide the
  // numeric < > <= >= operators since they only confuse users; user-defined
  // number/date fields get the full set.
  const operatorsForField = (fieldValue) => {
    if (ACTIVITY_FIELD_VALUES.has(fieldValue)) return ACTIVITY_OPERATORS;
    const meta = getFieldMeta(fieldValue);
    if (meta?.type === 'number' || meta?.type === 'date') return NUMERIC_OPERATORS;
    return TEXT_OPERATORS;
  };

  useEffect(() => {
    if (open) {
      setConditions([{ field: 'last_name', operator: '=', value: '' }]);
      setLogic('and');
      setResultCount(null);
    }
  }, [open]);

  const addCondition = () => {
    setConditions(prev => [...prev, { field: 'company', operator: '=', value: '' }]);
  };

  const removeCondition = (idx) => {
    if (conditions.length <= 1) return;
    setConditions(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx, key, value) => {
    setConditions(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const next = { ...c, [key]: value };
      // When switching field type, reset operator + value to sensible defaults
      // so the user doesn't have to think about it. The defaults are tuned per
      // field so picking "Email" auto-selects "contains", picking "Status" auto-
      // selects "equals", etc.
      if (key === 'field') {
        const isActivity = ACTIVITY_FIELD_VALUES.has(value);
        const wasActivity = ACTIVITY_FIELD_VALUES.has(c.field);
        if (isActivity && !wasActivity) {
          next.operator = 'days_since';
          next.value = '';
        } else if (!isActivity && wasActivity) {
          next.operator = '=';
          next.value = '';
        } else {
          // Smart per-field operator default (e.g. names → starts with,
          // email/phone/notes → contains, dropdowns → equals).
          const baseField = BASE_FIELDS.find(f => f.value === value);
          if (baseField?.defaultOperator) {
            next.operator = baseField.defaultOperator;
          } else if (baseField?.valueOptions || baseField?.dynamicOptions) {
            next.operator = '=';
          }
          next.value = '';
        }
      }
      return next;
    }));
  };

  const runLookup = async (overrideConditions = null, overrideSortKey = null) => {
    // "never" operator is the one case that doesn't need a value to be valid
    const baseConditions = overrideConditions || conditions;
    const validConditions = baseConditions.filter(c => c.operator === 'never' || c.value.trim());
    if (validConditions.length === 0) return;
    setSearching(true);
    try {
      const effectiveSortKey = overrideSortKey || sortKey;
      const [sortBy, sortOrder] = effectiveSortKey.split(':');
      const payload = {
        conditions: validConditions,
        logic,
        mode,
      };
      // When narrowing/adding, prefer sending the full original query so backend
      // re-runs against the ENTIRE DB (not just the 200 lazy-loaded IDs).
      // Fall back to current_ids ONLY when we actually have the complete set
      // loaded (length === total) — otherwise we'd silently narrow to the
      // ~200 lazy-loaded slice and miss the rest of the DB.
      if (mode === 'narrow' || mode === 'add') {
        if (currentQuery?.conditions?.length) {
          payload.previous_conditions = currentQuery.conditions;
          payload.previous_logic = currentQuery.logic || 'and';
        } else if (currentIds && currentIds.length > 0 && currentIds.length === currentTotal) {
          payload.current_ids = currentIds;
        }
        // else: no real "previous filter" — treat as a fresh search of the entire DB
      }
      const { data } = await api.post(
        `/contacts/lookup?page=1&limit=200&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}`,
        payload,
      );
      setResultCount(data.total);
      setLastResult({ ...data, _query: payload });
      if (onResults) onResults({ ...data, _query: payload });
    } catch (err) {
      console.error('Lookup failed', err);
    } finally {
      setSearching(false);
    }
  };

  const saveAsGroup = async (groupType) => {
    if (!lastResult) {
      toast.error('Run a lookup first');
      return;
    }
    const validConditions = conditions.filter(c => c.operator === 'never' || c.value.trim());
    const defaultName = groupType === 'smart'
      ? `Smart: ${validConditions.map(c => `${c.field} ${c.operator} ${c.value}`).join(' & ').slice(0, 50)}`
      : `Snapshot of ${resultCount} contacts`;
    const name = window.prompt(
      groupType === 'smart'
        ? "Name your Smart Group:\n\nThis saves the query — every time you open it, it re-runs and includes any newly-matching contacts."
        : "Name your Group:\n\nThis saves a frozen snapshot of the current contacts.",
      defaultName,
    );
    if (!name || !name.trim()) return;

    setSavingGroup(true);
    try {
      const ids = (lastResult.contacts || []).map(c => c.contact_id);
      const payload = {
        name: name.trim(),
        description: '',
        group_type: groupType,
        conditions: validConditions,
        logic,
        current_ids: groupType === 'static' ? ids : null,
      };
      const { data: created } = await api.post('/groups/from-lookup', payload);
      toast.success(
        groupType === 'smart'
          ? `✨ Smart group "${created.name}" saved (${created.member_count} contacts right now)`
          : `📁 Group "${created.name}" saved (${created.member_count} contacts)`,
        { duration: 5000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] rounded-none border-[#999] p-0 gap-0" data-testid="lookup-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 bg-[#E0E0E0] border-b border-[#999]">
          <DialogTitle className="text-[13px] font-bold text-[#333] flex items-center gap-2">
            <FunnelSimple size={16} weight="bold" className="text-[#0B5394]" />
            Lookup Contacts
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-3">
          {/* Lookup mode (ACT! 4.0 style) — only show if there's an existing lookup set */}
          {currentTotal > 0 && (
            <div className="flex flex-wrap items-center gap-3 bg-[#FFFBEA] border border-[#F0D87C] rounded p-2" data-testid="lookup-mode-row">
              <span className="text-[11px] font-bold text-[#7A4F00]">Current set: {currentTotal.toLocaleString()} contacts</span>
              <label className="flex items-center gap-1" title="Run the search against ALL contacts in the database, ignoring your current set.">
                <input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} className="w-3 h-3" data-testid="lookup-mode-new" />
                <span className="text-[11px] text-[#333]">Search all contacts</span>
              </label>
              <label className="flex items-center gap-1" title="Filter further within your current set — narrows down the results you already have.">
                <input type="radio" name="mode" checked={mode === 'narrow'} onChange={() => setMode('narrow')} className="w-3 h-3" data-testid="lookup-mode-narrow" />
                <span className="text-[11px] text-[#333]">Search within current results</span>
              </label>
              <label className="flex items-center gap-1" title="Combine the new matches with your current set (union).">
                <input type="radio" name="mode" checked={mode === 'add'} onChange={() => setMode('add')} className="w-3 h-3" data-testid="lookup-mode-add" />
                <span className="text-[11px] text-[#333]">Add results to current</span>
              </label>
            </div>
          )}

          {/* Logic toggle */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-medium text-[#333]">Match:</span>
            <label className="flex items-center gap-1">
              <input type="radio" name="logic" checked={logic === 'and'} onChange={() => setLogic('and')} className="w-3 h-3" />
              <span className="text-[11px] text-[#333]">ALL conditions (AND)</span>
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" name="logic" checked={logic === 'or'} onChange={() => setLogic('or')} className="w-3 h-3" />
              <span className="text-[11px] text-[#333]">ANY condition (OR)</span>
            </label>
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            {conditions.map((cond, idx) => (
              <div key={idx} className="flex items-center gap-1.5" data-testid={`lookup-condition-${idx}`}>
                {idx > 0 && (
                  <span className="text-[10px] text-[#999] w-6 text-center font-bold">{logic === 'and' ? 'AND' : 'OR'}</span>
                )}
                {idx === 0 && <span className="w-6" />}
                <select
                  value={cond.field}
                  onChange={(e) => updateCondition(idx, 'field', e.target.value)}
                  className="h-[28px] px-1.5 text-[12px] border border-[#B0B0B0] bg-white w-[140px]"
                  data-testid={`lookup-field-${idx}`}
                >
                  <optgroup label="Standard">
                    {BASE_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </optgroup>
                  <optgroup label="Activity History">
                    {ACTIVITY_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </optgroup>
                  {userFields.length > 0 && (
                    <optgroup label="Custom">
                      {userFields.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                    </optgroup>
                  )}
                </select>
                <select
                  value={cond.operator}
                  onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
                  className="h-[28px] px-1.5 text-[12px] border border-[#B0B0B0] bg-white w-[155px]"
                  data-testid={`lookup-operator-${idx}`}
                >
                  {(ACTIVITY_FIELD_VALUES.has(cond.field) ? ACTIVITY_OPERATORS : operatorsForField(cond.field)).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {(() => {
                  // Activity-history operator UI: "days_since" / "days_within" want a number;
                  // "never" needs no input
                  if (ACTIVITY_FIELD_VALUES.has(cond.field)) {
                    if (cond.operator === 'never') {
                      return (
                        <div className="flex-1 h-[28px] px-2 text-[12px] bg-[#F5F5F5] border border-[#B0B0B0] flex items-center text-[#888] italic">
                          no value needed
                        </div>
                      );
                    }
                    return (
                      <div className="flex-1 flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          value={cond.value}
                          onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                          placeholder="90"
                          className="w-20 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                          data-testid={`lookup-value-${idx}`}
                        />
                        <span className="text-[11px] text-[#666]">days</span>
                      </div>
                    );
                  }
                  // Built-in fields with a known fixed value set (e.g. Status)
                  // render as a dropdown so the user doesn't have to guess the
                  // exact stored value ("Lead" vs "lead" etc).
                  const baseField = BASE_FIELDS.find(f => f.value === cond.field);
                  // Auto-run on dropdown value pick when ONE condition with
                  // a value is set — saves a click for simple Status/State/Tag
                  // lookups (the most common case). We pass the new condition
                  // explicitly so runLookup doesn't see stale state.
                  const handleDropdownPick = (newVal) => {
                    updateCondition(idx, 'value', newVal);
                    if (newVal && conditions.length === 1) {
                      const nextConditions = conditions.map((c, i) =>
                        i === idx ? { ...c, value: newVal } : c
                      );
                      runLookup(nextConditions);
                    }
                  };
                  if (baseField?.valueOptions?.length) {
                    const opts = baseField.value === 'status' ? statusOptions.map((s) => s.label) : baseField.valueOptions;
                    return (
                      <select
                        value={cond.value}
                        onChange={(e) => handleDropdownPick(e.target.value)}
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      >
                        <option value="">— select —</option>
                        {opts.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    );
                  }
                  // Dynamic-options fields (e.g. Tags) pull their list from a
                  // backend query so newly-added values appear automatically.
                  if (baseField?.dynamicOptions === 'tags') {
                    return (
                      <select
                        value={cond.value}
                        onChange={(e) => handleDropdownPick(e.target.value)}
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      >
                        <option value="">— select a tag —</option>
                        {tagsList.length === 0 && <option disabled>(no tags in use yet)</option>}
                        {tagsList.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    );
                  }
                  const meta = getFieldMeta(cond.field);
                  const mtype = meta?.type;
                  if (mtype === 'dropdown' && meta.options?.length > 0) {
                    return (
                      <select
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      >
                        <option value="">— select —</option>
                        {meta.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    );
                  }
                  if (mtype === 'checkbox') {
                    return (
                      <select
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      >
                        <option value="">— any —</option>
                        <option value="yes">Yes</option>
                        <option value="">No / empty</option>
                      </select>
                    );
                  }
                  if (mtype === 'date') {
                    return (
                      <input
                        type="date"
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      />
                    );
                  }
                  if (mtype === 'number') {
                    return (
                      <input
                        type="number"
                        value={cond.value}
                        onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runLookup()}
                        placeholder="Number..."
                        className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                        data-testid={`lookup-value-${idx}`}
                      />
                    );
                  }
                  return (
                    <input
                      value={cond.value}
                      onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && runLookup()}
                      placeholder="Value..."
                      autoComplete="one-time-code"
                      className="flex-1 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                      autoFocus={idx === 0}
                      data-testid={`lookup-value-${idx}`}
                    />
                  );
                })()}
                {conditions.length > 1 && (
                  <button onClick={() => removeCondition(idx)} className="p-1 text-[#C0392B] hover:bg-red-50" data-testid={`lookup-remove-${idx}`}>
                    <Trash size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addCondition}
            className="flex items-center gap-1.5 text-[11px] font-medium text-[#0B5394] border border-dashed border-[#0B5394] bg-[#F0F7FF] hover:bg-[#E0EEFF] px-2.5 py-1.5 rounded"
            data-testid="lookup-add-condition"
            title="Add another condition to refine your search"
          >
            <Plus size={12} weight="bold" /> Add Another Condition
          </button>

          {/* Sort dropdown — only meaningful after a lookup has run */}
          {resultCount !== null && resultCount > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-[#E5E5E5]" data-testid="lookup-sort-row">
              <span className="text-[11px] text-[#666]">Sort results by:</span>
              <select
                value={sortKey}
                onChange={(e) => { setSortKey(e.target.value); runLookup(null, e.target.value); }}
                className="h-[26px] px-2 text-[11px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                data-testid="lookup-sort-select"
              >
                {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2.5 bg-[#E0E0E0] border-t border-[#999]">
          <div>
            {resultCount !== null && (
              <span
                className={`text-[12px] font-bold ${resultCount === 0 ? 'text-red-600' : 'text-green-700'}`}
                data-testid="lookup-result-count"
              >
                {resultCount === 0
                  ? '⚠ No matching contacts found — try widening your conditions'
                  : `✓ Found: ${resultCount.toLocaleString()} contact${resultCount !== 1 ? 's' : ''}`}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="px-3 py-1 text-[11px] font-medium border border-[#999] bg-white hover:bg-[#F0F0F0]"
            >
              Cancel
            </button>
            {resultCount !== null && resultCount > 0 && (
              <button
                onClick={() => saveAsGroup('static')}
                disabled={savingGroup}
                className="px-3 py-1 text-[11px] font-medium border border-[#0B5394] text-[#0B5394] bg-white hover:bg-[#E8F0FB] disabled:opacity-50 flex items-center gap-1"
                data-testid="lookup-save-static-btn"
                title="Save current contacts as a frozen list — the members are locked in and won't change."
              >
                <UsersThree size={12} weight="bold" />
                Save as List (frozen)
              </button>
            )}
            {resultCount !== null && resultCount > 0 && (
              <button
                onClick={() => saveAsGroup('smart')}
                disabled={savingGroup}
                className="px-3 py-1 text-[11px] font-medium border border-[#7A4F00] text-[#7A4F00] bg-[#FFFBEA] hover:bg-[#FFF4CC] disabled:opacity-50 flex items-center gap-1"
                data-testid="lookup-save-smart-btn"
                title="Save the QUERY as a smart group — re-runs every time you open it and includes any newly-matching contacts."
              >
                <Sparkle size={12} weight="bold" />
                Save as Smart Group (auto-updates)
              </button>
            )}
            <button
              onClick={() => runLookup()}
              disabled={searching || !conditions.some(c => c.operator === 'never' || c.value.trim())}
              className="px-4 py-1 text-[11px] font-medium border border-[#0B5394] text-[#0B5394] bg-white hover:bg-[#E8F0FB] disabled:opacity-50 flex items-center gap-1"
              data-testid="lookup-run-btn"
            >
              <MagnifyingGlass size={12} weight="bold" />
              {searching ? 'Searching...' : (resultCount !== null ? 'Re-run' : 'Lookup')}
            </button>
            {resultCount !== null && resultCount > 0 && (
              <button
                onClick={() => onOpenChange(false)}
                className="px-4 py-1 text-[11px] font-bold bg-[#0B5394] text-white hover:bg-[#073763] flex items-center gap-1"
                data-testid="lookup-apply-btn"
                title="Close this dialog and work with the filtered contacts"
              >
                Apply & Close
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
